import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Heart, Calendar, Phone, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmergencyEmpathy = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    calendly_url: '',
    emergency_empathy_title: 'Empatia de Emergência',
    emergency_empathy_description: 'Quando você precisa de apoio imediato em Simby - Inteligência Relacional, Paula está aqui para te ajudar a navegar situações desafiadoras'
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load Calendly widget script
  useEffect(() => {
    if (settings.calendly_url) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [settings.calendly_url]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center">
              <Heart className="w-8 h-8 text-rose-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {settings.emergency_empathy_title}
              </h1>
            </div>
          </div>

          <p className="text-lg text-gray-600 max-w-2xl">
            {settings.emergency_empathy_description}
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          <Card className="p-6 border-rose-200 bg-gradient-to-br from-white to-rose-50">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">O que você pode esperar</h3>
            </div>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <span className="text-rose-500 mr-2">•</span>
                Escuta empática e sem julgamentos
              </li>
              <li className="flex items-start">
                <span className="text-rose-500 mr-2">•</span>
                Apoio para processar emoções intensas
              </li>
              <li className="flex items-start">
                <span className="text-rose-500 mr-2">•</span>
                Ferramentas de Inteligência Relacional para a situação
              </li>
              <li className="flex items-start">
                <span className="text-rose-500 mr-2">•</span>
                Estratégias práticas para o momento
              </li>
            </ul>
          </Card>

          <Card className="p-6 border-rose-200 bg-gradient-to-br from-white to-rose-50">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Como funciona</h3>
            </div>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <span className="font-semibold text-rose-600 mr-2">1.</span>
                Escolha um horário disponível no calendário
              </li>
              <li className="flex items-start">
                <span className="font-semibold text-rose-600 mr-2">2.</span>
                Você receberá um link para a chamada
              </li>
              <li className="flex items-start">
                <span className="font-semibold text-rose-600 mr-2">3.</span>
                Paula entrará em contato no horário marcado
              </li>
              <li className="flex items-start">
                <span className="font-semibold text-rose-600 mr-2">4.</span>
                Sessão de 30 minutos de apoio focado
              </li>
            </ul>
          </Card>
        </div>

        {/* Calendly Embed */}
        <Card className="p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Calendar className="w-6 h-6 text-rose-600" />
            <h2 className="text-xl font-bold text-gray-900">Agende sua sessão</h2>
          </div>

          {settings.calendly_url ? (
            <div
              className="calendly-inline-widget"
              data-url={settings.calendly_url}
              style={{ minWidth: '320px', height: '700px' }}
            />
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">
                O agendamento estará disponível em breve.
              </p>
              <p className="text-sm text-gray-400">
                Entre em contato conosco enquanto isso.
              </p>
            </div>
          )}
        </Card>

        {/* Note */}
        <Card className="p-6 bg-amber-50 border-amber-200">
          <p className="text-amber-800 text-sm">
            <strong>Nota importante:</strong> Este serviço é um suporte complementar e não substitui
            acompanhamento psicológico ou psiquiátrico. Se você está em crise ou precisa de ajuda
            profissional urgente, por favor entre em contato com o CVV (188) ou procure ajuda especializada.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default EmergencyEmpathy;

